/**
 * Bandeja de autorizaciones — el AdmG acepta o declina desde la web lo que
 * hasta ahora solo podía resolver en el celular (pantalla Solicitudes).
 *
 * Misma tabla `autorizaciones` y mismos estados (Progreso / Aprobado /
 * Cancelado). Quien pidió la autorización se entera igual que con el
 * celular: las pantallas nuevas consultan el estado por polling y la
 * transferencia de chasis escucha el evento de Firebase, que aquí se publica
 * con la misma forma que la app móvil.
 *
 * El backend exige AdmG y solo resuelve lo que sigue en progreso, así que si
 * el solicitante canceló o la resolvió otro AdmG (web o celular) se avisa y
 * se refresca la lista en vez de pisarla.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Checkbox from "@mui/material/Checkbox";
import InputAdornment from "@mui/material/InputAdornment";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import { useGlobalMDAlert, useMaterialUIController } from "context";
import {
  FP,
  PageHeader,
  BarraVolver,
  StatusPill,
  EstadoVacio,
  FiltroPildoras,
  fpInputSx,
} from "components/financepro";
import { useApi } from "lib/api";
import { publicarEventoNegocio } from "lib/firebaseNegocio";

const TIPOS = {
  "Aut.Prestamo": "Préstamo",
  "Aut.Prestamomenor": "Préstamo menor",
  "Aut.Incauto": "Incauto",
  "Aut.Transf": "Transferencia",
  "Aut.LoteIncauto": "Lote de incautos",
};
const etiquetaTipo = (t) => TIPOS[t] || t || "Solicitud";

const ESTADOS = [
  { id: "Progreso", label: "Pendientes", color: FP.color.info },
  { id: "Aprobado", label: "Aprobadas", color: FP.color.success },
  { id: "Cancelado", label: "Declinadas / canceladas", color: FP.color.danger },
];
const PILL_ESTADO = {
  Progreso: { label: "Pendiente", variant: "info" },
  Aprobado: { label: "Aprobada", variant: "success" },
  Cancelado: { label: "Declinada", variant: "danger" },
};

// Etiquetas de los datos que mandan las pantallas al pedir la autorización;
// lo que no esté aquí se muestra con su nombre tal cual.
const CAMPOS = {
  nombre: "Nombre",
  apellido: "Apellido",
  cedula: "Cédula",
  edad: "Edad",
  chasis: "Chasis",
  marca: "Marca",
  modelo: "Modelo",
  color: "Color",
  precio: "Precio",
  inicial: "Inicial",
  financia: "Financia",
  plazo: "Plazo",
  formadepago: "Forma de pago",
  interes: "Interés",
  valorcuota: "Valor cuota",
  deudatotal: "Deuda total",
  valorincauto: "Valor incauto",
  lote: "Lote",
  incautador: "Incautador",
  motivo: "Motivo",
  prestamos: "Préstamos",
  totalvencido: "Total vencido",
  fechalimite: "Fecha límite",
  nota: "Nota",
};
const MONTOS = new Set([
  "precio",
  "inicial",
  "financia",
  "valorcuota",
  "deudatotal",
  "valorincauto",
  "totalvencido",
  "costo",
  "Valor",
]);
const OCULTOS = new Set(["Descripcion", "tipo", "motores", "comentariorecep"]);

const fmtMonto = (v) => {
  const n = parseFloat(String(v ?? "").replace(/,/g, ""));
  return Number.isNaN(n)
    ? String(v ?? "")
    : n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const fmtValor = (k, v) => {
  if (v === null || v === undefined || String(v).trim() === "") return "—";
  if (MONTOS.has(k)) return fmtMonto(v);
  if (k === "interes") return `${v}%`;
  return String(v);
};

const tarjetaSx = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function Fila({ etiqueta, valor }) {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      spacing={2}
      sx={{ py: 0.8, borderBottom: `1px solid ${FP.color.surfaceAlt}` }}
    >
      <Typography sx={{ fontSize: "0.82rem", color: FP.color.textSecondary }}>
        {etiqueta}
      </Typography>
      <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, textAlign: "right" }}>
        {valor}
      </Typography>
    </Stack>
  );
}
Fila.propTypes = { etiqueta: PropTypes.node.isRequired, valor: PropTypes.node.isRequired };

function Detalle({ aut, onAceptar, onDeclinar, procesando }) {
  const d = aut.detalle || {};
  // Lote de incautos: el AdmG puede desmarcar préstamos y aprobar el resto
  // en la misma decisión.
  const lineas = Array.isArray(aut.lineas) ? aut.lineas : [];
  const vigentes = lineas.filter((l) => !String(l.estado || "").trim());
  const [quitados, setQuitados] = useState([]);
  useEffect(() => setQuitados([]), [aut.idtext]);
  const alternar = (id) =>
    setQuitados((q) => (q.includes(id) ? q.filter((x) => x !== id) : [...q, id]));
  const editable = aut.status === "Progreso";
  const aprobados = vigentes.filter((l) => !quitados.includes(l.id));
  const campos = Object.keys(d).filter((k) => !OCULTOS.has(k) && typeof d[k] !== "object");
  const motores = Array.isArray(d.motores) ? d.motores : [];
  const pill = PILL_ESTADO[aut.status] || { label: aut.status, variant: "neutral" };

  return (
    <Box sx={{ ...tarjetaSx, p: { xs: 2, md: 2.6 } }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1, flexWrap: "wrap" }}>
        <StatusPill label={etiquetaTipo(aut.tipo)} variant="neutral" />
        <StatusPill label={pill.label} variant={pill.variant} />
      </Stack>
      <Typography sx={{ fontWeight: 800, fontSize: "1.02rem" }}>
        {aut.usuario} {d.Descripcion || "solicita autorización"}
      </Typography>
      <Typography sx={{ fontSize: "0.8rem", color: FP.color.textSecondary, mb: 2 }}>
        {aut.sucursal}
        {aut.fecha ? ` · ${aut.fecha}` : ""}
        {aut.receptor ? ` · Resuelta por ${aut.receptor}` : ""}
      </Typography>

      {aut.status === "Cancelado" && aut.comentario ? (
        <Box sx={{ p: 1.4, mb: 2, borderRadius: 2, bgcolor: FP.color.dangerSoft }}>
          <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: "#9F1239" }}>
            Motivo
          </Typography>
          <Typography sx={{ fontSize: "0.85rem" }}>{aut.comentario}</Typography>
        </Box>
      ) : null}

      {campos.length > 0 && (
        <Box sx={{ mb: 2 }}>
          {campos.map((k) => (
            <Fila key={k} etiqueta={CAMPOS[k] || k} valor={fmtValor(k, d[k])} />
          ))}
        </Box>
      )}

      {motores.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontWeight: 800, fontSize: "0.88rem", mb: 0.8 }}>
            Motores ({motores.length})
          </Typography>
          {motores.map((m, i) => (
            <Box
              // eslint-disable-next-line react/no-array-index-key
              key={`${m.Chasis || ""}-${i}`}
              sx={{ p: 1.2, mb: 0.8, borderRadius: 2, bgcolor: FP.color.surface }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                {m.Chasis || m.chasis || "—"}
              </Typography>
              <Typography sx={{ fontSize: "0.76rem", color: FP.color.textSecondary }}>
                {[m.Marca, m.Modelo, m.Color].filter(Boolean).join(" · ")}
                {m.Valor ? ` · Precio ${fmtMonto(m.Valor)}` : ""}
                {m.condicion ? ` · ${m.condicion}` : ""}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {lineas.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontWeight: 800, fontSize: "0.88rem", mb: 0.4 }}>
            Préstamos del lote (
            {editable ? `${aprobados.length} de ${vigentes.length} marcados` : lineas.length})
          </Typography>
          {editable && (
            <Typography sx={{ fontSize: "0.76rem", color: FP.color.textSecondary, mb: 0.8 }}>
              Desmarque los que no aprueba; al aceptar quedan rechazados y el resto aprobado.
            </Typography>
          )}
          <Box sx={{ maxHeight: 420, overflowY: "auto" }}>
            {lineas.map((l) => {
              const vigente = !String(l.estado || "").trim();
              const marcado = vigente && !quitados.includes(l.id);
              return (
                <Stack
                  key={l.id}
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{
                    p: 1,
                    mb: 0.6,
                    borderRadius: 2,
                    bgcolor: marcado || !editable ? FP.color.surface : FP.color.dangerSoft,
                    opacity: vigente ? 1 : 0.55,
                  }}
                >
                  {editable && (
                    <Checkbox
                      size="small"
                      checked={marcado}
                      disabled={!vigente || procesando}
                      onChange={() => alternar(l.id)}
                    />
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: "0.84rem" }}>
                      {l.noprest} · {l.cliente}
                    </Typography>
                    <Typography sx={{ fontSize: "0.75rem", color: FP.color.textSecondary }}>
                      {l.chasis}
                      {l.sucursal ? ` · Suc. ${l.sucursal}` : ""}
                      {vigente ? "" : " · ya no está pendiente"}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "right" }}>
                    <Typography sx={{ fontWeight: 700, fontSize: "0.82rem" }}>
                      {fmtMonto(l.vencido)}
                    </Typography>
                    <Typography sx={{ fontSize: "0.72rem", color: FP.color.textSecondary }}>
                      {l.dias} días · {l.cuotas} cuotas
                    </Typography>
                  </Box>
                </Stack>
              );
            })}
          </Box>
        </Box>
      )}

      {aut.status === "Progreso" && (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
          <Button
            variant="contained"
            disableElevation
            onClick={() => onAceptar(quitados)}
            disabled={procesando || (vigentes.length > 0 && aprobados.length === 0)}
            startIcon={
              procesando ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <CheckCircleRoundedIcon />
              )
            }
            sx={{
              flex: 1,
              textTransform: "none",
              fontWeight: 700,
              bgcolor: FP.color.success,
              color: "#FFFFFF !important",
              "&:hover": { bgcolor: FP.color.successDark },
            }}
          >
            {quitados.length ? `Aceptar ${aprobados.length} de ${vigentes.length}` : "Aceptar"}
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={onDeclinar}
            disabled={procesando}
            startIcon={<CancelRoundedIcon />}
            sx={{
              flex: 1,
              textTransform: "none",
              fontWeight: 700,
              bgcolor: FP.color.danger,
              color: "#FFFFFF !important",
              "&:hover": { bgcolor: "#BE123C" },
            }}
          >
            Declinar
          </Button>
        </Stack>
      )}
    </Box>
  );
}
Detalle.propTypes = {
  aut: PropTypes.shape({
    idtext: PropTypes.string,
    tipo: PropTypes.string,
    status: PropTypes.string,
    usuario: PropTypes.string,
    sucursal: PropTypes.string,
    fecha: PropTypes.string,
    receptor: PropTypes.string,
    comentario: PropTypes.string,
    detalle: PropTypes.objectOf(PropTypes.any),
    lineas: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  onAceptar: PropTypes.func.isRequired,
  onDeclinar: PropTypes.func.isRequired,
  procesando: PropTypes.bool.isRequired,
};

export default function BandejaAutorizaciones({ onExit, onCambio, refrescarToken }) {
  const { post } = useApi();
  const { showAlert } = useGlobalMDAlert();
  const [{ usuarioLog }] = useMaterialUIController();

  const [estado, setEstado] = useState("Progreso");
  const [filas, setFilas] = useState([]);
  const [pendientes, setPendientes] = useState(0);
  const [cargando, setCargando] = useState(true);
  const [buscar, setBuscar] = useState("");
  const [selId, setSelId] = useState(null);
  const [procesando, setProcesando] = useState(false);
  const [declinarOpen, setDeclinarOpen] = useState(false);
  const [motivo, setMotivo] = useState("");

  const cargar = useCallback(
    async (silencioso = false) => {
      if (!silencioso) setCargando(true);
      try {
        const { ok, data } = await post("/api/autorizaciones_bandeja", { estado });
        if (ok) {
          setFilas(data.data || []);
          setPendientes(data.pendientes || 0);
          if (onCambio) onCambio(data.pendientes || 0);
        } else if (!silencioso) {
          showAlert({
            message: data.Error || "No se pudieron leer las autorizaciones",
            color: "error",
          });
        }
      } finally {
        setCargando(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [post, estado]
  );

  useEffect(() => {
    setSelId(null);
    cargar();
  }, [cargar]);

  // Llegó una autorización nueva o la resolvieron en otro lado.
  useEffect(() => {
    if (refrescarToken) cargar(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refrescarToken]);

  const visibles = useMemo(() => {
    const q = buscar.trim().toLowerCase();
    if (!q) return filas;
    return filas.filter((a) =>
      [
        a.usuario,
        a.sucursal,
        etiquetaTipo(a.tipo),
        a.detalle?.Descripcion,
        a.detalle?.nombre,
        a.detalle?.apellido,
        a.detalle?.chasis,
        a.detalle?.cedula,
        a.detalle?.incautador,
        ...(a.lineas || []).flatMap((l) => [l.noprest, l.cliente, l.chasis]),
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [filas, buscar]);

  const seleccionada = visibles.find((a) => a.idtext === selId) || visibles[0] || null;

  const resolver = async (accion, comentario = "", excluir = []) => {
    if (!seleccionada) return;
    setProcesando(true);
    try {
      const { ok, status, data } = await post("/api/autorizaciones_resolver", {
        idtext: seleccionada.idtext,
        accion,
        comentario,
        excluir,
      });
      if (ok) {
        const aceptar = accion === "aceptar";
        // Mismo evento que publica el celular: lo escucha la transferencia de
        // chasis y refresca la pantalla Solicitudes de la app.
        publicarEventoNegocio({
          accion: aceptar ? "Aprobar" : "Declinar",
          descripcion: {
            Descripcion: `${usuarioLog} ${aceptar ? "acepto" : "rechazo"} la autorizacion`,
            tipo: seleccionada.tipo,
            ...(aceptar ? {} : { comentariorecep: comentario }),
          },
          modulo: "Autorizaciones",
          sucursal: seleccionada.sucursal,
          usuario: usuarioLog,
        });
        showAlert({
          message: aceptar ? "Autorización aceptada" : "Autorización declinada",
          color: "success",
        });
        setDeclinarOpen(false);
        setMotivo("");
      } else {
        showAlert({
          message: data.Error || "No se pudo actualizar la autorización",
          color: status === 409 ? "warning" : "error",
        });
      }
      setSelId(null);
      cargar(true);
    } finally {
      setProcesando(false);
    }
  };

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      {onExit && <BarraVolver onExit={onExit} />}
      <PageHeader
        title="Autorizaciones"
        subtitle="Solicitudes de préstamos, incautos y transferencias que esperan a un AdmG"
      />
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          alignItems={{ md: "center" }}
          sx={{ mb: 2 }}
        >
          <FiltroPildoras
            valor={estado}
            onChange={setEstado}
            opciones={ESTADOS.map((e) => ({
              id: e.id,
              label: e.label,
              color: e.color,
              conteo: e.id === "Progreso" ? pendientes : undefined,
            }))}
            sx={{ flex: 1 }}
          />
          <TextField
            size="small"
            placeholder="Buscar usuario, sucursal, cliente, chasis..."
            value={buscar}
            onChange={(e) => setBuscar(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon sx={{ fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
            sx={{ ...fpInputSx, minWidth: { md: 320 } }}
          />
          <Button
            onClick={() => cargar()}
            startIcon={<RefreshRoundedIcon />}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Actualizar
          </Button>
        </Stack>

        {cargando && (
          <Stack alignItems="center" sx={{ py: 8 }}>
            <CircularProgress size={30} />
          </Stack>
        )}
        {!cargando && visibles.length === 0 && (
          <Box sx={tarjetaSx}>
            <EstadoVacio
              icono={FactCheckOutlinedIcon}
              titulo={
                estado === "Progreso" ? "No hay autorizaciones pendientes" : "No hay autorizaciones"
              }
              subtitulo={
                estado === "Progreso"
                  ? "Cuando alguien pida una autorización aparecerá aquí."
                  : undefined
              }
            />
          </Box>
        )}
        {!cargando && visibles.length > 0 && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "minmax(280px, 380px) 1fr" },
              gap: 2,
              alignItems: "start",
            }}
          >
            <Box sx={{ ...tarjetaSx, overflow: "hidden" }}>
              {visibles.map((a) => {
                const activa = seleccionada && a.idtext === seleccionada.idtext;
                return (
                  <Box
                    key={a.idtext}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelId(a.idtext)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") setSelId(a.idtext);
                    }}
                    sx={{
                      px: 2,
                      py: 1.4,
                      cursor: "pointer",
                      borderBottom: `1px solid ${FP.color.surfaceAlt}`,
                      borderLeft: `3px solid ${activa ? FP.color.accentBlue : "transparent"}`,
                      bgcolor: activa ? FP.color.accentBlueSoft : "transparent",
                      "&:hover": { bgcolor: activa ? FP.color.accentBlueSoft : FP.color.surface },
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" spacing={1}>
                      <Typography sx={{ fontWeight: 800, fontSize: "0.85rem" }}>
                        {etiquetaTipo(a.tipo)}
                      </Typography>
                      <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted }}>
                        {a.fecha}
                      </Typography>
                    </Stack>
                    <Typography
                      sx={{
                        fontSize: "0.8rem",
                        color: FP.color.textSecondary,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {a.usuario} · {a.sucursal}
                    </Typography>
                    {a.detalle?.incautador ? (
                      <Typography
                        sx={{
                          fontSize: "0.76rem",
                          color: FP.color.textMuted,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {a.detalle.incautador} · {a.detalle.prestamos} préstamo(s)
                      </Typography>
                    ) : null}
                    {a.detalle?.nombre || a.detalle?.chasis ? (
                      <Typography
                        sx={{
                          fontSize: "0.76rem",
                          color: FP.color.textMuted,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {[
                          [a.detalle.nombre, a.detalle.apellido].filter(Boolean).join(" "),
                          a.detalle.chasis,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </Typography>
                    ) : null}
                  </Box>
                );
              })}
            </Box>
            {seleccionada && (
              <Detalle
                aut={seleccionada}
                procesando={procesando}
                onAceptar={(quitados) => resolver("aceptar", "", quitados || [])}
                onDeclinar={() => {
                  setMotivo("");
                  setDeclinarOpen(true);
                }}
              />
            )}
          </Box>
        )}
      </Box>

      <Dialog
        open={declinarOpen}
        onClose={() => !procesando && setDeclinarOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Declinar autorización</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Motivo"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            inputProps={{ maxLength: 250 }}
            sx={{ mt: 1, ...fpInputSx }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeclinarOpen(false)}
            disabled={procesando}
            sx={{ textTransform: "none" }}
          >
            Volver
          </Button>
          <Button
            variant="contained"
            disableElevation
            disabled={procesando || !motivo.trim()}
            onClick={() => resolver("declinar", motivo.trim())}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              bgcolor: FP.color.danger,
              color: "#FFFFFF !important",
              "&:hover": { bgcolor: "#BE123C" },
            }}
          >
            Declinar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

BandejaAutorizaciones.propTypes = {
  onExit: PropTypes.func,
  onCambio: PropTypes.func,
  refrescarToken: PropTypes.number,
};

BandejaAutorizaciones.defaultProps = {
  onExit: null,
  onCambio: null,
  refrescarToken: 0,
};
